<template>
    <v-navigation-drawer
        app
        permanent
        floating
        :mini-variant="effectiveMiniVariant"
        :mini-variant-width="100"
        :width="280"
        class="pa-2"
        color="transparent"
        :class="[{ dim: dimmedSidebar, 'navigation-drawer--mini': effectiveMiniVariant }]">
        <v-card flat class="fill-height d-flex flex-column">
            <v-list nav dense>
                <v-list-item v-if="!effectiveMiniVariant">
                    <v-list-item-action class="my-0">
                        <v-btn text color="transparent" class="px-2 ml-n2" :to="{ name: 'welcome' }">
                            <v-img contain max-width="100" src="@/assets/Nuvolos-transparent.svg" />
                        </v-btn>
                    </v-list-item-action>
                    <v-spacer></v-spacer>
                    <v-list-item-action class="my-0">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text min-width="0" class="px-2" color="secondary" v-bind="attrs" v-on="on" @click.stop="toggleMiniMode">
                                    <v-icon>mdi-dock-left</v-icon>
                                </v-btn>
                            </template>
                            <span>Minimize sidebar</span>
                        </v-tooltip>
                    </v-list-item-action>
                </v-list-item>

                <v-list-item v-if="effectiveMiniVariant" class="justify-center px-0">
                    <v-list-item-action class="my-0 mx-auto">
                        <v-btn min-width="36" height="36" text color="transparent" :to="{ name: 'welcome' }">
                            <v-img contain max-width="26" src="@/assets/N-Nuvolos-blue.svg" />
                        </v-btn>
                    </v-list-item-action>
                </v-list-item>

                <v-list-item v-if="effectiveMiniVariant" class="justify-center px-0">
                    <v-list-item-action class="my-0 mx-auto">
                        <v-tooltip right>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn text color="secondary" min-width="36" height="36" v-bind="attrs" v-on="on" @click.stop="toggleMiniMode">
                                    <v-icon>mdi-dock-left</v-icon>
                                </v-btn>
                            </template>
                            <span>Expand sidebar</span>
                        </v-tooltip>
                    </v-list-item-action>
                </v-list-item>
            </v-list>

            <v-divider></v-divider>

            <div style="overflow-y: auto" :key="navigationSectionsRenderKey">
                <DrawerOrganizationSection
                    v-if="isOrgRelatedRoute && !isOrgConfigRoute && !isUserSettingsRoute"
                    :collapsed="effectiveMiniVariant"
                    :expanded.sync="showOrganizationItems" />

                <DrawerUserSettingsSection v-if="isUserSettingsRoute" :collapsed="effectiveMiniVariant" :expanded.sync="showUserSettingsItems" />

                <DrawerOrganizationSettingsSection v-if="isOrgConfigRoute" :collapsed="effectiveMiniVariant" :expanded.sync="showOrganizationSettingsItems" />

                <!-- <v-divider v-if="isOrgConfigRoute"></v-divider> -->

                <DrawerSpaceSettingsSection v-if="isOrgConfigRoute" :collapsed="effectiveMiniVariant" :expanded.sync="showOrgSpaceSettingsItems" />

                <!-- <v-divider v-if="isOrgConfigRoute && isConfigSpaceSelected"></v-divider> -->

                <DrawerInstanceSettingsSection
                    v-if="isOrgConfigRoute && isConfigSpaceSelected"
                    :collapsed="effectiveMiniVariant"
                    :expanded.sync="showOrgInstanceSettingsItems" />

                <DrawerSpaceSection v-if="isSnapshotContext" :collapsed="effectiveMiniVariant" :expanded.sync="showSpaceItems" />

                <!-- <v-divider v-if="isSnapshotContext"></v-divider> -->

                <DrawerInstanceSection v-if="isSnapshotContext" :collapsed="effectiveMiniVariant" :expanded.sync="showInstanceItems" />

                <!-- <v-divider v-if="isSnapshotContext"></v-divider> -->

                <DrawerSnapshotSection v-if="isSnapshotContext" :collapsed="effectiveMiniVariant" :expanded.sync="showSnapshotItems" />

                <DrawerRunningApplicationsSection
                    v-if="isSnapshotContext && hasRunningApplications"
                    :collapsed="effectiveMiniVariant"
                    :expanded.sync="showRunningApplicationsItems" />
            </div>

            <v-spacer></v-spacer>

            <v-divider></v-divider>

            <v-list nav dense v-if="isSnapshotContext && isOndemandGPUForStudents">
                <instance-usage-card :collapsed="effectiveMiniVariant" />
            </v-list>

            <v-divider v-if="isSnapshotContext && isOndemandGPUForStudents"></v-divider>

            <v-list
                nav
                dense
                v-if="isSnapshotContext && !fetchingSnapshotQuota && snapshotQuota.bytes_pcent !== null && isDevelopment && snapshotFilesystemPrefix">
                <storage-usage-dialog>
                    <v-hover v-if="effectiveMiniVariant" v-slot="{ hover }">
                        <v-list-item class="justify-center px-0 pointer" :class="{ 'grey lighten-4': hover }">
                            <v-list-item-action class="mx-auto">
                                <v-tooltip right>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-progress-circular
                                            v-bind="attrs"
                                            v-on="on"
                                            :rotate="-90"
                                            :size="36"
                                            :width="4"
                                            :value="quotaPercentageUsage"
                                            :color="quotaPercentageUsage <= 90 ? 'primary' : 'error'">
                                            <span class="caption">{{ Math.floor(quotaPercentageUsage) }}</span>
                                        </v-progress-circular>
                                    </template>
                                    <span>{{ `${humanFileSize(snapshotQuota.bytes_used)} of ${humanFileSize(snapshotQuota.bytes_quota)} used` }}</span>
                                </v-tooltip>
                            </v-list-item-action>
                        </v-list-item>
                    </v-hover>
                    <v-hover v-else v-slot="{ hover }">
                        <v-card class="pointer" flat :class="{ 'grey lighten-4': hover }">
                            <v-card-text class="py-3">
                                <div class="d-flex align-center">
                                    <span class="caption font-weight-medium">Storage</span>
                                    <v-spacer></v-spacer>
                                    <span class="caption">{{ `${quotaPercentageUsage}%` }}</span>
                                </div>
                                <v-progress-linear
                                    rounded
                                    height="6"
                                    :value="quotaPercentageUsage"
                                    :color="quotaPercentageUsage <= 90 ? 'primary' : 'error'"
                                    class="mb-2"></v-progress-linear>
                                <div class="caption font-weight-medium d-flex align-center">
                                    {{ `${humanFileSize(snapshotQuota.bytes_used)} of ${humanFileSize(snapshotQuota.bytes_quota)}` }} used
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-hover>
                </storage-usage-dialog>
            </v-list>

            <v-divider v-if="isSnapshotContext"></v-divider>

            <v-list nav dense>
                <v-list-item v-if="isOrgManager && currentOrgOid" active-class="primary--text" :to="settingsRoute">
                    <v-list-item-icon>
                        <v-tooltip right :disabled="!effectiveMiniVariant">
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">mdi-cog-outline</v-icon>
                            </template>
                            <span>Settings</span>
                        </v-tooltip>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Settings</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <ToolbarAccountMenu v-if="$auth.isAuthenticated" :collapsed="effectiveMiniVariant" />
            </v-list>
        </v-card>
    </v-navigation-drawer>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { humanFileSize } from '@/utils'
import { intercomMethods } from '@/mixins/intercom'
import { enumsData } from '@/mixins/enums'
const DrawerOrganizationSection = () => import('@/components/navigationDrawer/sections/DrawerOrganizationSection.vue')
const DrawerUserSettingsSection = () => import('@/components/navigationDrawer/sections/DrawerUserSettingsSection.vue')
const DrawerOrganizationSettingsSection = () => import('@/components/navigationDrawer/sections/DrawerOrganizationSettingsSection.vue')
const DrawerSpaceSettingsSection = () => import('@/components/navigationDrawer/sections/DrawerSpaceSettingsSection.vue')
const DrawerInstanceSettingsSection = () => import('@/components/navigationDrawer/sections/DrawerInstanceSettingsSection.vue')
const DrawerSpaceSection = () => import('@/components/navigationDrawer/sections/DrawerSpaceSection.vue')
const DrawerInstanceSection = () => import('@/components/navigationDrawer/sections/DrawerInstanceSection.vue')
const DrawerSnapshotSection = () => import('@/components/navigationDrawer/sections/DrawerSnapshotSection.vue')
const DrawerRunningApplicationsSection = () => import('@/components/navigationDrawer/sections/DrawerRunningApplicationsSection.vue')
const InstanceUsageCard = () => import('@/modules/snapshot/components/InstanceUsageCard.vue')
const StorageUsageDialog = () => import('@/modules/snapshot/components/StorageUsageDialog.vue')
const ToolbarAccountMenu = () => import('@/components/ToolbarAccountMenu.vue')

export default {
    name: 'NavigationDrawer',
    mixins: [intercomMethods, enumsData],
    components: {
        DrawerOrganizationSection,
        DrawerUserSettingsSection,
        DrawerOrganizationSettingsSection,
        DrawerSpaceSettingsSection,
        DrawerInstanceSettingsSection,
        DrawerSpaceSection,
        DrawerInstanceSection,
        DrawerSnapshotSection,
        DrawerRunningApplicationsSection,
        InstanceUsageCard,
        StorageUsageDialog,
        ToolbarAccountMenu
    },
    data() {
        const miniModel = localStorage.getItem('nv-navigation-drawer-mini') === '1'

        return {
            showOrganizationItems: true,
            showOrganizationSettingsItems: true,
            showOrgSpaceSettingsItems: true,
            showOrgInstanceSettingsItems: true,
            showUserSettingsItems: true,
            showSpaceItems: true,
            showSnapshotItems: true,
            showInstanceItems: true,
            showRunningApplicationsItems: true,
            miniModel
        }
    },
    methods: {
        humanFileSize,
        syncConfigSectionExpansion() {
            if (!this.isOrgConfigRoute) {
                return
            }

            const routeName = this.$route.name || ''
            this.showOrganizationSettingsItems = routeName === 'config-root' || routeName.startsWith('config-org-')
            this.showOrgSpaceSettingsItems = routeName.startsWith('config-space')
            this.showOrgInstanceSettingsItems = routeName.startsWith('config-instance')
        },
        toggleMiniMode() {
            this.miniModel = !this.miniModel
        }
    },
    computed: {
        ...mapGetters('orgStore', ['isOrgManager']),
        ...mapGetters('appStore', ['runningDeployments', 'startingDeployments']),
        ...mapGetters('spaceStore', ['currentSpaceType']),
        ...mapGetters('snapshotStore', ['isDevelopment', 'snapshotFilesystemPrefixById']),
        ...mapState(['userOrgs', 'currentOrgData', 'dimmedSidebar']),
        ...mapState('snapshotStore', ['applications', 'snapshotQuota', 'fetchingSnapshotQuota']),
        ...mapState('spaceStore', ['currentSpace']),
        effectiveMiniVariant() {
            return this.miniModel
        },
        navigationSectionsRenderKey() {
            const routeName = this.$route.name || ''
            const { oid, sid, iid, snid } = this.$route.params
            return [routeName, oid || '', sid || '', iid || '', snid || '', this.isOrgConfigRoute ? 'config' : 'non-config'].join('|')
        },
        isSnapshotContext() {
            return Boolean(this.$route.params.sid && this.$route.params.iid && this.$route.params.snid)
        },
        isOrgRelatedRoute() {
            return Boolean(!this.$route.params.sid)
        },
        isOrgConfigRoute() {
            const routeName = this.$route.name || ''
            return (
                routeName === 'config-root' ||
                routeName.startsWith('config-org-') ||
                routeName.startsWith('config-space') ||
                routeName.startsWith('config-instance')
            )
        },
        isUserSettingsRoute() {
            const routeName = this.$route.name || ''
            return [
                'user-settings',
                'user-profile',
                'user-ssh',
                'user-secrets',
                'user-env-vars',
                'user-tables',
                'user-dropbox',
                'user-api-access',
                'user-nuvolos-settings'
            ].includes(routeName)
        },
        isConfigSpaceSelected() {
            return Boolean(this.$route.params.sid)
        },
        isConfigInstanceSelected() {
            return Boolean(this.$route.params.iid)
        },
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        settingsRoute() {
            if (this.activeOrgSpaceId) {
                return {
                    name: 'config-space-general',
                    params: { oid: this.currentOrgOid, sid: this.activeOrgSpaceId }
                }
            }

            return {
                name: 'config-org-general',
                params: { oid: this.currentOrgOid }
            }
        },
        activeOrgSpaceId() {
            return this.$route.params.sid
        },
        activeOrgInstanceId() {
            return this.$route.params.iid
        },
        isOndemandGPUForStudents() {
            return (
                (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE &&
                    this.currentSpace.space_configs?.on_demand_gpu_for_students &&
                    this.isDevelopment) ||
                false
            )
        },
        quotaPercentageUsage() {
            if (this.snapshotQuota) {
                return this.snapshotQuota.bytes_pcent || 0
            }
            return null
        },
        snapshotFilesystemPrefix() {
            return this.snapshotFilesystemPrefixById(this.$route.params.snid)
        },
        hasRunningApplications() {
            if (!this.isSnapshotContext) {
                return false
            }

            const availableApps = this.applications || []
            const deploymentApps = (this.runningDeployments || []).concat(this.startingDeployments || [])

            return deploymentApps.some(app => {
                return availableApps.some(available => available.aid.toString() === app.aid.toString())
            })
        }
    },
    watch: {
        '$route.name': {
            immediate: true,
            handler() {
                this.syncConfigSectionExpansion()
            }
        },
        miniModel(value) {
            localStorage.setItem('nv-navigation-drawer-mini', value ? '1' : '0')
        }
    }
}
</script>

<style lang="scss" scoped>
.dim:not(img) {
    filter: invert(100%) hue-rotate(180deg);
}

.navigation-drawer--mini ::v-deep .v-subheader {
    display: none;
}

.navigation-drawer--mini ::v-deep .v-btn {
    justify-content: center !important;
}

.navigation-drawer--mini ::v-deep .v-btn__content > span {
    display: none;
}

.navigation-drawer--mini ::v-deep .v-btn__content .v-icon {
    margin-right: 0 !important;
}

.sidebar-logo-link::before {
    opacity: 0 !important;
}

.sidebar-logo-link:hover {
    background-color: transparent !important;
}

::v-deep .drawer-active-item.primary--text {
    position: relative;
    background-color: transparent !important;
}

::v-deep .drawer-active-item.primary--text::before,
::v-deep .drawer-active-item.v-list-item--active::before {
    opacity: 0 !important;
}

::v-deep .drawer-active-item.primary--text::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -8px;
    width: 5px;
    border-radius: 2px;
    background-color: var(--v-primary-base);
    opacity: 0.8;
    z-index: 2;
    pointer-events: none;
}
</style>
